import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, XCircle, Clock, Loader2, ChevronDown, ArrowRight } from 'lucide-react';
import { upgradeRequestApi } from '@/lib/api';

interface UpgradeRequest {
  id:            string;
  tenant_id:     string;
  from_plan:     string;
  to_plan:       string;
  status:        'pending' | 'approved' | 'rejected' | 'cancelled';
  note:          string | null;
  admin_note:    string | null;
  processed_at:  string | null;
  created_at:    string;
  hospital_name: string;
  schema_name:   string;
  contact_email: string;
}

const STATUS_STYLES: Record<string, string> = {
  pending:   'bg-amber-50 text-amber-700 border-amber-200',
  approved:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected:  'bg-red-50 text-red-700 border-red-200',
  cancelled: 'bg-gray-50 text-gray-500 border-gray-200',
};

const PLAN_LABEL: Record<string, string> = { base: 'Base', plus: 'Plus', pro: 'Pro' };

function fmtDate(d: string) {
  try { return new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return d; }
}

// ── Action modal ──────────────────────────────────────────────────
function ActionModal({ request, action, onClose }: {
  request: UpgradeRequest;
  action:  'approve' | 'reject';
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');

  const mut = useMutation({
    mutationFn: () => upgradeRequestApi.process(request.id, action, note || undefined),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['upgrade-requests'] }); onClose(); },
  });

  const isApprove = action === 'approve';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-4
                         ${isApprove ? 'bg-emerald-50' : 'bg-red-50'}`}>
          {isApprove
            ? <CheckCircle2 size={20} className="text-emerald-600"/>
            : <XCircle size={20} className="text-red-600"/>}
        </div>

        <h2 className="text-navy font-semibold text-lg mb-1">
          {isApprove ? 'Approve upgrade' : 'Reject upgrade'}
        </h2>
        <p className="text-sm text-gray-400 mb-4">
          <strong>{request.hospital_name}</strong> — {PLAN_LABEL[request.from_plan] ?? request.from_plan}
          {' '}<ArrowRight size={12} className="inline"/>{' '}
          {PLAN_LABEL[request.to_plan] ?? request.to_plan}
        </p>

        {isApprove && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-700 mb-4">
            Approving will immediately update their subscription plan and enable the new module set.
          </div>
        )}

        <label className="block text-xs font-medium text-gray-500 mb-1">
          Note to hospital <span className="text-gray-300">(optional)</span>
        </label>
        <textarea
          rows={3}
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder={isApprove ? 'Your plan has been upgraded. Please refresh the portal.' : 'Please contact support for more details.'}
          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm resize-none
                     focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange mb-4"
        />

        <div className="flex gap-3">
          <button onClick={onClose}
            className="flex-1 py-2 rounded-lg border border-gray-200 text-sm text-gray-500 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={() => mut.mutate()}
            disabled={mut.isPending}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2
                        disabled:opacity-40 ${isApprove
                          ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                          : 'bg-red-600 text-white hover:bg-red-700'}`}>
            {mut.isPending && <Loader2 size={14} className="animate-spin"/>}
            {isApprove ? 'Approve & Apply' : 'Reject Request'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────
export default function UpgradeRequestsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('pending');
  const [modal, setModal] = useState<{ request: UpgradeRequest; action: 'approve' | 'reject' } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['upgrade-requests', statusFilter],
    queryFn:  () => upgradeRequestApi.list({ status: statusFilter }).then(r => r.data),
  });

  const requests: UpgradeRequest[] = data?.rows ?? [];

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-navy">Upgrade Requests</h1>
          <p className="text-sm text-gray-400 mt-0.5">Hospital admin requests for plan changes</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="appearance-none pr-7 pl-3 py-1.5 text-xs rounded-lg border border-gray-200
                         focus:outline-none focus:ring-2 focus:ring-orange/30 bg-white text-gray-600">
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
              <option value="all">All</option>
            </select>
            <ChevronDown size={12} className="absolute right-2 top-2.5 text-gray-400 pointer-events-none"/>
          </div>
          <button onClick={() => qc.invalidateQueries({ queryKey: ['upgrade-requests'] })}
            className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500">
            Refresh
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-orange"/></div>
      ) : requests.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Clock size={32} className="mx-auto mb-3 opacity-40"/>
          <p className="text-sm">No {statusFilter === 'all' ? '' : statusFilter} requests</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map(req => (
            <div key={req.id} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-navy">{req.hospital_name}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${STATUS_STYLES[req.status]}`}>
                      {req.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{req.contact_email} · {req.schema_name}</p>
                </div>
                <div className="text-right shrink-0">
                  <div className="flex items-center gap-2 justify-end">
                    <span className="text-sm font-semibold text-navy">
                      {PLAN_LABEL[req.from_plan] ?? req.from_plan}
                    </span>
                    <ArrowRight size={14} className="text-orange"/>
                    <span className="text-sm font-semibold text-orange">
                      {PLAN_LABEL[req.to_plan] ?? req.to_plan}
                    </span>
                  </div>
                  <p className="text-xs text-gray-300 mt-0.5">{fmtDate(req.created_at)}</p>
                </div>
              </div>

              {req.note && (
                <div className="mt-3 bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-600">
                  <span className="font-medium text-gray-400 mr-1">Hospital note:</span>
                  {req.note}
                </div>
              )}

              {req.admin_note && (
                <div className="mt-2 bg-blue-50 rounded-lg px-3 py-2 text-xs text-blue-700">
                  <span className="font-medium mr-1">Admin note:</span>
                  {req.admin_note}
                </div>
              )}

              {req.status === 'pending' && (
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => setModal({ request: req, action: 'approve' })}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                               bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100">
                    <CheckCircle2 size={12}/>
                    Approve
                  </button>
                  <button
                    onClick={() => setModal({ request: req, action: 'reject' })}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                               bg-red-50 text-red-600 border border-red-200 hover:bg-red-100">
                    <XCircle size={12}/>
                    Reject
                  </button>
                </div>
              )}

              {req.processed_at && (
                <p className="text-xs text-gray-300 mt-3">
                  Processed {fmtDate(req.processed_at)}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {modal && (
        <ActionModal
          request={modal.request}
          action={modal.action}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
